import { Injectable, signal } from '@angular/core';
import { ChangelogRelease } from './changelog.models';

const INDEX_URL = '/assets/changelog/index.json';
const RELEASE_URL_PREFIX = '/assets/changelog/';
const LAST_SEEN_STORAGE_KEY = 'threadcrm.changelog.lastSeenId';
/** Filenames must match YYYY-MM-DD-vX.Y.Z.md so fetches stay inside /assets/changelog. */
const SAFE_RELEASE_FILE = /^\d{4}-\d{2}-\d{2}-v[\w.-]+\.md$/;

@Injectable({ providedIn: 'root' })
export class ChangelogService {
  readonly hasUnseenRelease = signal(false);

  private indexCache: ChangelogRelease[] | null = null;
  private indexRequest: Promise<ChangelogRelease[]> | null = null;

  async getIndex(): Promise<ChangelogRelease[]> {
    if (this.indexCache) {
      return this.indexCache;
    }
    if (this.indexRequest) {
      return this.indexRequest;
    }

    this.indexRequest = this.fetchIndex()
      .then((releases) => {
        this.indexCache = releases;
        this.updateUnseenState(releases);
        return releases;
      })
      .finally(() => {
        this.indexRequest = null;
      });

    return this.indexRequest;
  }

  async getReleaseMarkdown(file: string): Promise<string> {
    if (!SAFE_RELEASE_FILE.test(file)) {
      throw new Error('Unknown release file');
    }

    const response = await fetch(`${RELEASE_URL_PREFIX}${file}`, { cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`Failed to load release notes (${response.status})`);
    }
    return response.text();
  }

  async refreshUnseenState(): Promise<void> {
    try {
      const releases = await this.getIndex();
      this.updateUnseenState(releases);
    } catch {
      this.hasUnseenRelease.set(false);
    }
  }

  markLatestSeen(releases = this.indexCache): void {
    const latestId = releases?.[0]?.id;
    if (!latestId) {
      this.hasUnseenRelease.set(false);
      return;
    }
    this.writeLastSeenId(latestId);
    this.hasUnseenRelease.set(false);
  }

  private async fetchIndex(): Promise<ChangelogRelease[]> {
    const response = await fetch(`${INDEX_URL}?_=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`Failed to load changelog index (${response.status})`);
    }

    const payload = (await response.json()) as unknown;
    if (!Array.isArray(payload)) {
      return [];
    }

    return payload.flatMap((item) => {
      const release = this.toRelease(item);
      return release ? [release] : [];
    });
  }

  private toRelease(value: unknown): ChangelogRelease | null {
    if (!value || typeof value !== 'object') {
      return null;
    }
    const item = value as Record<string, unknown>;
    const id = item['id'];
    const version = item['version'];
    const date = item['date'];
    const title = item['title'];
    const file = item['file'];
    const codename = item['codename'];
    const image = item['image'];
    const excerpt = item['excerpt'];
    if (
      typeof id !== 'string' ||
      typeof version !== 'string' ||
      typeof date !== 'string' ||
      typeof title !== 'string' ||
      typeof file !== 'string' ||
      !SAFE_RELEASE_FILE.test(file)
    ) {
      return null;
    }

    return {
      id,
      version,
      date,
      title,
      codename: typeof codename === 'string' ? codename : '',
      image: typeof image === 'string' ? image : '',
      excerpt: typeof excerpt === 'string' ? excerpt : '',
      file,
    };
  }

  private updateUnseenState(releases: ChangelogRelease[]): void {
    const latestId = releases[0]?.id;
    if (!latestId) {
      this.hasUnseenRelease.set(false);
      return;
    }
    this.hasUnseenRelease.set(this.readLastSeenId() !== latestId);
  }

  private readLastSeenId(): string {
    try {
      return localStorage.getItem(LAST_SEEN_STORAGE_KEY)?.trim() || '';
    } catch {
      return '';
    }
  }

  private writeLastSeenId(id: string): void {
    try {
      localStorage.setItem(LAST_SEEN_STORAGE_KEY, id);
    } catch {
      /* ignore quota / private mode */
    }
  }
}
